import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Star, Users, Globe, Award, Calendar as CalendarIcon, LogOut } from 'lucide-react';
import { MENTOR_PROFILE, STUDENT_DASHBOARD } from '@/constants/testIds';
import { BookingModal } from '@/components/BookingModal';
import { Skeleton } from '@/components/ui/skeleton';
import { getStudentSession, clearStudentSession } from '@/lib/auth';
import { BrandLogo } from '@/components/BrandLogo';

export default function MentorProfilePage() {
    const { mentorId } = useParams();
    const [mentor, setMentor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [session, setSession] = useState(getStudentSession());
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/mentors/${mentorId}`);
                setMentor(data);
            } catch (e) {
                setMentor(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [mentorId]);

    const logout = () => {
        clearStudentSession();
        setSession(null);
        navigate('/');
    };

    const TopBar = () => (
        <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
            <div className="mx-auto flex h-16 w-full max-w-5xl items-center justify-between px-4 sm:px-6">
                <BrandLogo size="md" to="/" />
                <div className="flex items-center gap-2 text-xs">
                    {session ? (
                        <>
                            <span className="hidden text-muted-foreground sm:inline">{session.student?.email}</span>
                            <Button variant="ghost" size="sm" onClick={logout} data-testid={STUDENT_DASHBOARD.logout}>
                                <LogOut className="mr-1 h-4 w-4" /> Log out
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" size="sm" onClick={() => navigate('/')}>Sign in</Button>
                    )}
                </div>
            </div>
        </header>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <TopBar />
                <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
                    <Skeleton className="h-40 w-full rounded-xl" />
                    <Skeleton className="mt-4 h-24 w-full rounded-xl" />
                </div>
            </div>
        );
    }

    if (!mentor) {
        return (
            <div className="min-h-screen bg-background">
                <TopBar />
                <div className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6">
                    <h1 className="font-display text-2xl font-semibold">Mentor not found</h1>
                    <p className="mt-1 text-muted-foreground">The mentor you’re looking for doesn’t exist.</p>
                    <Button className="mt-4" onClick={() => navigate('/')}>Back to mentors</Button>
                </div>
            </div>
        );
    }

    const initials = mentor.name?.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

    return (
        <div className="min-h-screen bg-background">
            <TopBar />
            <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
                <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Back to mentors
                </Link>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
                    <Card className="border-border/60 p-6 sm:p-8">
                        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
                            <Avatar className="h-24 w-24 border border-border/60">
                                <AvatarImage src={mentor.avatar_url} alt={mentor.name} />
                                <AvatarFallback className="bg-secondary text-primary text-xl">{initials}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                                <h1 className="font-display text-3xl font-semibold tracking-tight text-primary">{mentor.name}</h1>
                                <p className="mt-1 text-muted-foreground">{mentor.headline}</p>
                                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                                    <span className="flex items-center gap-1">
                                        <Star className="h-4 w-4 fill-current text-amber-500" />
                                        <span className="font-semibold tabular-nums">{mentor.rating?.toFixed?.(1) ?? 'N/A'}</span>
                                        <span className="text-muted-foreground">rating</span>
                                    </span>
                                    <span className="flex items-center gap-1 text-muted-foreground">
                                        <Users className="h-4 w-4" />
                                        <span className="font-medium text-foreground tabular-nums">{mentor.sessions_completed}</span>
                                        <span>sessions</span>
                                    </span>
                                    <span className="flex items-center gap-1 text-muted-foreground">
                                        <Award className="h-4 w-4" />
                                        <span className="font-medium text-foreground tabular-nums">{mentor.years_experience}+</span>
                                        <span>yrs experience</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {mentor.tagline && (
                            <blockquote className="mt-6 rounded-lg border-l-4 border-accent bg-secondary px-4 py-3 font-display text-lg italic text-primary">
                                “{mentor.tagline}”
                            </blockquote>
                        )}

                        <div className="mt-6">
                            <h2 className="font-display text-lg font-semibold text-primary">About</h2>
                            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{mentor.bio}</p>
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expertise</h3>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {mentor.expertise?.map((e) => (
                                        <Badge key={e} variant="secondary" className="font-normal" data-testid="expertise-chip">{e}</Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Languages</h3>
                                <div className="mt-2 flex items-center gap-2 text-sm">
                                    <Globe className="h-4 w-4 text-muted-foreground" />
                                    {mentor.languages?.join(', ')}
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Booking sidebar */}
                    <aside className="lg:sticky lg:top-24 lg:self-start">
                        <Card className="border-border/60 p-5 shadow-sm">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="font-display text-2xl font-semibold text-primary">1 credit</div>
                                    <div className="text-xs text-muted-foreground">per session</div>
                                </div>
                                <Badge className="bg-accent text-accent-foreground hover:bg-accent">
                                    <CalendarIcon className="mr-1 h-3 w-3" />
                                    Booking open
                                </Badge>
                            </div>
                            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                                <div>✓ Real-time availability</div>
                                <div>✓ Confirmation via email {mentor.calendar_connected ? '(with Google Meet)' : ''}</div>
                                <div>✓ Cancel + reschedule per admin policy</div>
                            </div>
                            <Button
                                data-testid={MENTOR_PROFILE.book}
                                className="mt-5 w-full"
                                size="lg"
                                onClick={() => setModalOpen(true)}
                            >
                                Book a session
                            </Button>
                        </Card>
                    </aside>
                </div>
            </main>

            <BookingModal mentor={mentor} open={modalOpen} onOpenChange={setModalOpen} />
        </div>
    );
}
